import { FormEvent, useState } from 'react'
import { Link } from 'react-router-dom'

import * as api from 'api/api'
import { Button, TextField } from 'components/ui'
import { Icon } from 'components/Icon'
import { errorMessage } from 'lib/hooks'

import { AuthShell } from './AuthShell'

const COPY = {
  ar: {
    title: 'نسيت كلمة المرور؟',
    subtitle: 'أدخل بريدك الإلكتروني في JoPath وسنرسل لك رابطاً لإعادة تعيين كلمة المرور.',
    email: 'البريد الإلكتروني',
    submit: 'إرسال رابط إعادة التعيين',
    back: 'العودة لتسجيل الدخول',
    sentTitle: 'تحقق من بريدك الإلكتروني',
    sentBody: (email: string) =>
      `إذا كان هناك حساب مرتبط بـ ${email}، فسنرسل إليه رابطاً لإعادة تعيين كلمة المرور. الرابط صالح لمدة 30 دقيقة.`,
    resendHint: 'لم تستلم الرسالة؟ تحقق من البريد المزعج أو',
    resend: 'أعد الإرسال',
  },
  en: {
    title: 'Forgot your password?',
    subtitle: "Enter the email you use for JoPath and we'll send you a link to reset your password.",
    email: 'Email',
    submit: 'Send reset link',
    back: 'Back to sign in',
    sentTitle: 'Check your email',
    sentBody: (email: string) =>
      `If an account exists for ${email}, we sent a link to reset your password. The link expires in 30 minutes.`,
    resendHint: "Didn't receive it? Check spam or",
    resend: 'resend the email',
  },
} as const

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.requestPasswordReset(email)
      setSent(true)
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell>
      {(locale) => {
        const t = COPY[locale]

        if (sent) {
          return (
            <>
              <span className="auth__icon-badge" aria-hidden="true">
                <Icon name="send" size={20} />
              </span>
              <div className="auth__head auth__head--center">
                <h1>{t.sentTitle}</h1>
                <p>{t.sentBody(email)}</p>
              </div>
              <Link to="/login" className="btn btn--primary btn--block">
                {t.back}
              </Link>
              <p className="auth__footer">
                {t.resendHint}{' '}
                <button type="button" className="link-button" onClick={submit} disabled={busy}>
                  {t.resend}
                </button>
              </p>
            </>
          )
        }

        return (
          <>
            <span className="auth__wordmark">JoPath</span>
            <div className="auth__head">
              <h1>{t.title}</h1>
              <p>{t.subtitle}</p>
            </div>
            <form className="stack" onSubmit={submit} noValidate>
              {error ? (
                <div className="alert alert--error" role="alert">
                  {error}
                </div>
              ) : null}
              <TextField
                label={t.email}
                type="email"
                autoComplete="username"
                dir="ltr"
                value={email}
                disabled={busy}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
              <Button type="submit" variant="primary" busy={busy} disabled={!email} className="btn--block">
                {t.submit}
              </Button>
            </form>
            <Link to="/login" className="back-link auth__back">
              <Icon name="chevronRight" size={16} />
              {t.back}
            </Link>
          </>
        )
      }}
    </AuthShell>
  )
}
